import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  styled,
  Container,
  Button as MuiButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
} from "firebase/auth";
import Toast from "react-hot-toast";
import Logo from "../../assets/images/Logo.png";
import Phone from "../../assets/icons/Phone.png";
import TextBox from "../../components/TextField";
import Button from "../../components/Button";
import Alert from "../../components/Alert";
import Routes from "../../utils/Routes";
import OtpInput from 'react-otp-input';

function VerifyPhoneNumberScreen({ className, handlePhoneVerified }) {
  const navigate = useNavigate();
  const auth = getAuth();
  const [phone, setPhone] = useState("");
  // const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const timeOutCallback = useCallback(() => {
    setTimer((currTimer) => (currTimer > 0 ? currTimer - 1 : 0));
  }, []);

  const otpRefs = useRef([]);

  useEffect(() => {
    otpRefs.current = otpRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(timeOutCallback, 1000);
      return () => clearTimeout(timeout);
    }
  }, [timer, timeOutCallback]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next box if filled
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const isValidPhoneNumber = (phone) => {
    const sanitizedPhone = phone.replace(/[^0-9]/g, "");
    return sanitizedPhone.length === 10 && !/^(\d)\1{9}$/.test(sanitizedPhone);
  };

  const verifyPhoneNumber = async () => {
    if (phone.length !== 10) {
      setAlert({
        open: true,
        message: "Please enter a valid 10-digit phone number.",
        severity: "warning",
      });
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      setAlert({
        open: true,
        message: "Please enter a valid phone number",
        severity: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);

      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        }
      );

      const confirmation = await signInWithPhoneNumber(
        auth,
        `+1${phone}`,
        window.recaptchaVerifier
      );

      setVerificationId(confirmation.verificationId);
      Toast.success("OTP Sent! Please check your phone.");
    } catch (error) {
      Toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const otpSubmitHandler = async () => {
    if (otp.length !== 6) {
      setAlert({
        open: true,
        message: "Please enter a 6-digit OTP",
        severity: "warning",
      });
      return;
    }

    if (!verificationId) {
      setAlert({
        open: true,
        message: "Verification ID is missing. Please resend OTP.",
        severity: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);
      const credential = PhoneAuthProvider.credential(
        verificationId,
        otp
      );

      if (auth.currentUser) {
        // Link phone number to existing account
        await linkWithCredential(auth.currentUser, credential);
        if (auth.currentUser.phoneNumber) {
          handlePhoneVerified();
          navigate("/" + Routes.Home, { replace: true });
        }
      } else {
        // User is signing in with phone number
        await auth.signInWithCredential(credential);
      }

      Toast.success("Phone number verified successfully!");
      navigate("/" + Routes.Home);
    } catch (error) {
      setIsLoading(false);
      if (error.code === "auth/credential-already-in-use") {
        Toast.error(
          "This phone number is linked to another account. Please enter a different number."
        );
      } else {
        Toast.error(`OTP Verification Failed: ${error.message}`);
      }
    }
  };

  const resendOtpHandler = async () => {
    if (timer === 0) {
      setTimer(300);
      setIsLoading(true);
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
          }
        );

        const confirmation = await signInWithPhoneNumber(
          auth,
          `+1${phone}`,
          window.recaptchaVerifier
        );

        setAlert({
          open: true,
          message: "OTP Sent Successfully!",
          severity: "success",
        });
      } catch (error) {
        setAlert({
          open: true,
          message: error.message,
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className={className}>
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }}>
        <Alert
          open={alert.open}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        />
        <Box display="flex" flexDirection="column" alignItems="center">
          <img src={Logo} alt="Logo" className="logo" />
          <div className="heading">Verify Phone Number</div>

          {verificationId ? (
            <>
              {/*               <Box className="otpContainer">
                {otp.map((value, index) => (
                  <TextBox
                    key={index}
                    className="otpInput"
                    value={value}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    inputRef={(el) => (otpRefs.current[index] = el)}
                    maxLength={1}
                  />
                ))}
              </Box> */}
              <Box className="otpContainer">
                <OtpInput
                  className="otpContainer"
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  renderSeparator={<span> &nbsp;&nbsp;&nbsp;</span>}
                  inputType="tel"
                  shouldAutoFocus={true}
                  renderInput={(props) => <input {...props} />}
                  inputStyle="otpInput"
                />
              </Box>
              <Box className="resendContainer">
                <Typography variant="body2">Didn't receive OTP?</Typography>
                {timer === 0 ? (
                  <MuiButton
                    variant="text"
                    className="resendBtn"
                    onClick={resendOtpHandler}
                  >
                    Resend
                  </MuiButton>
                ) : (
                  <Typography variant="body2" sx={{ color: "green" }}>
                    {Math.floor(timer / 60)}:
                    {timer % 60 < 10 ? `0${timer % 60}` : timer % 60}s
                  </Typography>
                )}
              </Box>
              <Button
                label="Submit"
                onClick={otpSubmitHandler}
                loading={isLoading}
              />
            </>
          ) : (
            <>
              <TextBox
                placeholder="Enter Phone Number"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
                }
                icon={Phone}
              />
              <Button
                label="Submit"
                onClick={verifyPhoneNumber}
                disabled={isLoading || phone.length < 10}
                loading={isLoading}
              />
            </>
          )}
        </Box>
        <button id="recaptcha-container" style={{ display: "none" }}></button>
      </Container>
    </div>
  );
}

export default styled(VerifyPhoneNumberScreen)(() => ({
  ".logo": {
    width: "100px",
    height: "100px",
  },
  ".heading": {
    fontWeight: 600,
    fontSize: 22,
    letterSpacing: 0,
    textAlign: "center",
    color: "#234000",
  },
  ".otpContainer": {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "20px",
    marginBottom: "20px",
  },
  ".otpInput": {
    width: "35px !important",
    height: "50px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "bold",
    backgroundColor: "rgba(127, 168, 135, 0.2)",
    padding: "0px 0px !important",
    border: "none",
    outlineColor: "transparent",
  },
  ".resendContainer": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  ".resendBtn": {
    textTransform: "none",
    padding: 0,
    color: "#000000",
    fontWeight: 500,
  },
}));
